import { createSelector } from '@reduxjs/toolkit';

import { selectConversationsQuota } from '../conversationsQuota';
import { selectOneChatSettings } from '../oneChatSettings';
import { selectFlags } from '../flags';
import { CurrentStatus } from '../oneChatSettings/model';
import { selectMessages } from '../messages';

export const maybeWidgetOnline = createSelector(
  selectOneChatSettings.currentStatus,
  selectConversationsQuota.isExceeding,
  (status, isExceeding) => {
    if (!status || isExceeding === undefined) {
      return;
    }

    switch (status) {
      case CurrentStatus.OFFLINE:
        return false;
      case CurrentStatus.MANUAL_ONLINE:
        return true;
      case CurrentStatus.AI_ASSISTANT_ONLINE:
        return !isExceeding;
    }
  },
);

export const shouldShowWidget = createSelector(
  selectOneChatSettings.offlineBehavior,
  maybeWidgetOnline,
  (behavior, isOnline) => {
    return (
      isOnline || (behavior !== undefined && behavior.hideWidget === false)
    );
  },
);

const contactOrHasVisibleForm = createSelector(
  selectFlags.isContact,
  selectMessages.noContactFormOnLastPage,
  (isContact, noContactFormOnLastPage) => isContact || !noContactFormOnLastPage,
);

export const manualShouldShowContactForm = createSelector(
  selectOneChatSettings.isManual,
  selectOneChatSettings.manualContactFormBefore,
  contactOrHasVisibleForm,
  (isManual, behaviorRequiresForm, noNeedContactForm) =>
    !noNeedContactForm && isManual && behaviorRequiresForm,
);

export const aiShouldShowContactForm = createSelector(
  selectOneChatSettings.isAi,
  selectOneChatSettings.aiContactFormBefore,
  contactOrHasVisibleForm,
  (isAi, behaviorRequiresForm, noNeedContactForm) =>
    !noNeedContactForm && isAi && behaviorRequiresForm,
);

export const offlineShouldShowContactForm = createSelector(
  maybeWidgetOnline,
  shouldShowWidget,
  selectOneChatSettings.offlineBehavior,
  contactOrHasVisibleForm,
  (online, show, behavior, noNeedContactForm) =>
    !online && show && !noNeedContactForm && Boolean(behavior?.showContactForm),
);

export const shouldShowContactFormInIntroSequence = createSelector(
  manualShouldShowContactForm,
  aiShouldShowContactForm,
  offlineShouldShowContactForm,
  (manual, ai, offline) => manual || ai || offline,
);

export const shouldSendContactFormAfterFirstMessage = createSelector(
  selectOneChatSettings.isManual,
  selectFlags.isContact,
  selectOneChatSettings.manualContactFormAfterMessage,
  selectMessages.hasAnyVisitorMessages,
  selectMessages.noContactFormOnLastPage,
  (
    isManual,
    isContact,
    manualContactFormAfterMessage,
    hasAnyVisitorMessages,
    noContactFormOnLastPage,
  ) =>
    !isContact &&
    isManual &&
    manualContactFormAfterMessage &&
    !hasAnyVisitorMessages &&
    noContactFormOnLastPage,
);

export const offlineDisabledMessaging = createSelector(
  maybeWidgetOnline,
  selectOneChatSettings.offlineBehavior,
  (online, behavior) =>
    !online && behavior?.hideWidget === false && behavior?.allowChat === false,
);

export const shouldDisableInputWhenOffline = createSelector(
  maybeWidgetOnline,
  shouldShowWidget,
  selectOneChatSettings.offlineBehavior,
  (online, show, behavior) => !online && show && !behavior?.allowChat,
);

export const shouldDisableInputWhenManual = createSelector(
  manualShouldShowContactForm,
  selectOneChatSettings.manualAllowChat,
  (shouldShowContact, allowChat) => shouldShowContact && !allowChat,
);

export const shouldDisableInput = createSelector(
  shouldDisableInputWhenOffline,
  shouldDisableInputWhenManual,
  (offlineDisabled, manualDisabled) => offlineDisabled || manualDisabled,
);

export const aiIntroMessage = createSelector(
  selectOneChatSettings.aiIntroMessageSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (introMessageSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    introMessageSettings?.enabled &&
    introMessageSettings?.text,
);

export const aiLegalMessage = createSelector(
  selectOneChatSettings.aiLegalMessageSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (legalMessageSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    legalMessageSettings?.enabled &&
    legalMessageSettings?.text,
);

export const engageMessage = createSelector(
  selectOneChatSettings.engageMessageSettings,
  maybeWidgetOnline,
  (engageMessageSettings, online) =>
    online && engageMessageSettings?.enabled && engageMessageSettings?.text,
);

export const offlineIntroMessage = createSelector(
  selectOneChatSettings.offlineIntroMessageSettings,
  maybeWidgetOnline,
  (offlineMessageSettings, online) =>
    !online && offlineMessageSettings?.enabled && offlineMessageSettings?.text,
);

export const manualIntroMessage = createSelector(
  selectOneChatSettings.manualIntroMessageSettings,
  selectOneChatSettings.currentStatus,
  (introMessageSettings, status) =>
    status === CurrentStatus.MANUAL_ONLINE &&
    introMessageSettings?.enabled &&
    introMessageSettings?.text,
);

export const shouldAutoOpenWidget = createSelector(
  selectOneChatSettings.autoOpen,
  maybeWidgetOnline,
  (autoOpen, isOnline) => Boolean(autoOpen && isOnline),
);

export const aiSuggestions = createSelector(
  selectOneChatSettings.suggestionsSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (suggestionsSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.questions &&
    suggestionsSettings?.questions.length > 0
      ? suggestionsSettings.questions
      : undefined,
);

export const aiSuggestionsIntroMessage = createSelector(
  selectOneChatSettings.suggestionsSettings,
  selectOneChatSettings.currentStatus,
  maybeWidgetOnline,
  (suggestionsSettings, status, online) =>
    status === CurrentStatus.AI_ASSISTANT_ONLINE &&
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.introduction?.text
      ? suggestionsSettings.introduction.text
      : undefined,
);

export const shouldSendSuggestions = createSelector(
  selectMessages.shouldSendSuggestions,
  aiSuggestions,
  (shouldSend, suggestions) => shouldSend && Boolean(suggestions),
);
